import { useMemo } from 'react';
import type { CareStateLabOrderT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { toDayKeyYYYYMMDD, toTimestampMs } from '@/shared/lib';
import { toTableCellText } from '@/shared/lib/table';
import {
  buildDaySections,
  EmptyState,
  formatTimelineTimeLabel,
  humanizeTimelineStatusLabel,
  mapToDaySections,
  TimelineViewer,
} from '@/shared/ui/primitives';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/ui/shadcn/sheet';
import { Table, TableBody, TableCell, TableRow } from '@/shared/ui/shadcn/table';

import { getLabOrderDisplayName } from '../model/getLabOrderDisplayName';

export interface LabOrderDetailsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: CareStateLabOrderT | null;
}

function getLabOrderTitle(order: CareStateLabOrderT | null): string {
  if (!order) return 'Lab order';
  return getLabOrderDisplayName(order.order_data.value.lab_test);
}

type TimelineItem = TimelineItemMeta;

interface TimelineItemMeta {
  __key: string;
  __timestampMs: number | null;
  __dayKey: string | null;
  __timeLabel: string;
  __label: string;
}

function renderTimelineItem(item: TimelineItem) {
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-sm font-medium text-foreground">{item.__label}</div>
        <div className="shrink-0 text-xs text-muted-foreground">{item.__timeLabel}</div>
      </div>
    </div>
  );
}

export function LabOrderDetailsSheet({ open, onOpenChange, order }: LabOrderDetailsSheetProps) {
  const title = getLabOrderTitle(order);
  const metadata = order?.order_data.value.metadata ?? null;
  const details = metadata?.details ?? null;

  const curatedRows = useMemo(() => {
    if (!order) return [];

    const rows: Array<{ label: string; value: React.ReactNode }> = [];
    const markers = order.order_data.value.lab_test.markers;

    rows.push({
      label: 'Ordered',
      value: toTableCellText(order.ordered_at),
    });

    rows.push({
      label: 'Status',
      value: toTableCellText(order.status),
    });

    rows.push({
      label: 'Interpretation',
      value: toTableCellText(order.interpretation),
    });

    rows.push({
      label: 'Provider',
      value: toTableCellText(order.provider),
    });

    const biomarkerChips =
      markers.length > 0 ? (
        <span className="flex flex-wrap gap-1">
          {[...markers]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((m) => (
              <span key={m.id} className="rounded-full bg-muted px-2 py-0.5 text-xs">
                {m.name}
              </span>
            ))}
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      );

    rows.push({
      label: 'Biomarkers',
      value: biomarkerChips,
    });

    if (metadata?.sample_id) {
      rows.push({
        label: 'Sample ID',
        value: toTableCellText(metadata.sample_id),
      });
    }

    rows.push({
      label: 'Priority',
      value: metadata?.priority ? (
        <Badge variant="secondary" className="rounded-full">
          Priority
        </Badge>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
    });

    if (metadata?.notes) {
      rows.push({
        label: 'Notes',
        value: (
          <div className="whitespace-pre-wrap text-sm text-foreground">
            {toTableCellText(metadata.notes)}
          </div>
        ),
      });
    }

    if (details?.type) {
      rows.push({
        label: 'Order type',
        value: (
          <Badge variant="secondary" className="rounded-full">
            {toTableCellText(details.type)}
          </Badge>
        ),
      });
    }

    if (details?.type === 'testkit') {
      const shipment = details.shipment ?? null;
      const outbound = shipment?.outbound ?? null;
      const inbound = shipment?.inbound ?? null;

      const addTrackingRows = (prefix: string, tracking: typeof outbound) => {
        const trackingNumber = tracking?.tracking_number ?? null;
        const trackingUrl = tracking?.tracking_url ?? null;
        const courier = tracking?.courier ?? null;

        if (trackingNumber) {
          rows.push({ label: `${prefix} tracking #`, value: toTableCellText(trackingNumber) });
        }
        if (trackingUrl) {
          rows.push({
            label: `${prefix} tracking URL`,
            value: (
              <a
                href={trackingUrl}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-primary underline-offset-4 hover:underline"
              >
                {trackingUrl}
              </a>
            ),
          });
        }
        if (courier) {
          rows.push({ label: `${prefix} courier`, value: toTableCellText(courier) });
        }
      };

      addTrackingRows('Outbound', outbound);
      addTrackingRows('Inbound', inbound);
    }

    return rows;
  }, [details, metadata, order]);

  const timelineSections = useMemo(() => {
    if (!order) return [];
    const events = metadata?.events ?? [];

    if (events.length > 0) {
      return mapToDaySections(events, (event, index): TimelineItem => {
        const raw = event.created_at ?? null;
        const ms = toTimestampMs(raw);
        const dayKey = toDayKeyYYYYMMDD(raw);
        return {
          __key: `${index}:${event.remote_id}:${raw ?? ''}:${event.status ?? ''}`,
          __timestampMs: ms,
          __dayKey: dayKey,
          __timeLabel: formatTimelineTimeLabel(ms, raw),
          __label: humanizeTimelineStatusLabel(String(event.status ?? '')),
        };
      });
    }

    const fallbackItems: TimelineItem[] = [
      {
        __key: `fallback:ordered:${order.id}`,
        __timestampMs: toTimestampMs(order.ordered_at),
        __dayKey: toDayKeyYYYYMMDD(order.ordered_at),
        __timeLabel: formatTimelineTimeLabel(toTimestampMs(order.ordered_at), order.ordered_at),
        __label: 'Ordered',
      },
      {
        __key: `fallback:updated:${order.id}`,
        __timestampMs: toTimestampMs(order.updated_at),
        __dayKey: toDayKeyYYYYMMDD(order.updated_at),
        __timeLabel: formatTimelineTimeLabel(toTimestampMs(order.updated_at), order.updated_at),
        __label: 'Last updated',
      },
      {
        __key: `fallback:status:${order.id}`,
        __timestampMs: toTimestampMs(order.updated_at),
        __dayKey: toDayKeyYYYYMMDD(order.updated_at),
        __timeLabel: formatTimelineTimeLabel(toTimestampMs(order.updated_at), order.updated_at),
        __label: `Status: ${toTableCellText(order.status)}`,
      },
    ];

    return buildDaySections(fallbackItems);
  }, [metadata?.events, order]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex flex-col sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="text-lg font-semibold leading-tight">{title}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-auto px-4 pb-4">
          {!order ? (
            <div className="py-6">
              <EmptyState
                title="No lab order selected"
                description="Select a lab order to view details."
              />
            </div>
          ) : (
            <div className="space-y-6 py-4">
              <section>
                <Table>
                  <TableBody>
                    {curatedRows.map((row) => (
                      <TableRow key={row.label}>
                        <TableCell className="w-[140px] align-top text-xs font-medium text-muted-foreground">
                          {row.label}
                        </TableCell>
                        <TableCell className="align-top">{row.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </section>

              <section>
                <div className="text-sm font-semibold text-foreground">Timeline</div>
                <div className="pt-2">
                  <TimelineViewer
                    sections={timelineSections}
                    getItemKey={(item) => item.__key}
                    renderItem={renderTimelineItem}
                    empty={
                      <EmptyState
                        title="No timeline"
                        description="No lifecycle events were found for this lab order."
                      />
                    }
                  />
                </div>
              </section>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
